import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Shield, Lock, AlertTriangle, CheckCircle, XCircle, Calendar, Globe, RefreshCw, ExternalLink } from 'lucide-react';
import { useSEO } from '../hooks/useSEO';
import { toast } from 'react-hot-toast';
import AdContainer from '../components/ads/AdContainer';
import { GOOGLE_ADSENSE_CONFIG } from '../config/adsConfig';

interface SSLCertificate {
  domain: string;
  isValid: boolean;
  issuer: string;
  subject: string;
  validFrom: string;
  validTo: string;
  daysUntilExpiry: number;
  algorithm: string;
  keySize: number;
  serialNumber: string;
  fingerprint: string;
  subjectAlternativeNames: string[];
  isWildcard: boolean;
  isExtendedValidation: boolean;
  trustChain: Array<{
    subject: string;
    issuer: string;
    validTo: string;
  }>;
  vulnerabilities: Array<{
    type: string;
    severity: 'low' | 'medium' | 'high' | 'critical';
    description: string;
  }>;
}

const SSLCertificateChecker: React.FC = () => {
  const [url, setUrl] = useState('');
  const [isChecking, setIsChecking] = useState(false);
  const [certificate, setCertificate] = useState<SSLCertificate | null>(null);
  const [error, setError] = useState<string | null>(null);

  useSEO({
    title: 'SSL Certificate Checker - Verify Website Security',
    description: 'Check SSL certificate validity, expiration dates, and security details. Verify HTTPS security for any website with detailed certificate information.',
    keywords: 'SSL certificate checker, HTTPS checker, certificate validation, SSL security, certificate expiry, website security',
    canonical: 'https://snap-tools.online/seo/ssl-certificate-checker'
  });

  // Check SSL certificate
  const checkSSLCertificate = async () => {
    if (!url) {
      toast.error('Please enter a URL to check');
      return;
    }

    // Extract domain from URL
    let domain = url;
    try {
      if (!url.startsWith('http://') && !url.startsWith('https://')) {
        domain = 'https://' + url;
      }
      const urlObj = new URL(domain);
      domain = urlObj.hostname;
    } catch (error) {
      toast.error('Please enter a valid URL');
      return;
    }

    setIsChecking(true);
    setError(null);
    setCertificate(null);

    try {
      // In a real implementation, you would use a backend API to check SSL certificates
      // For demo purposes, we'll simulate the SSL check
      await new Promise(resolve => setTimeout(resolve, 2000));

      // Mock SSL certificate data
      const mockCertificate: SSLCertificate = {
        domain,
        isValid: Math.random() > 0.2, // 80% chance of valid certificate
        issuer: 'Let\'s Encrypt Authority X3',
        subject: `CN=${domain}`,
        validFrom: '2024-01-15T00:00:00Z',
        validTo: '2024-07-15T23:59:59Z',
        daysUntilExpiry: Math.floor(Math.random() * 90) + 1,
        algorithm: 'RSA-SHA256',
        keySize: 2048,
        serialNumber: '03:B2:F1:9F:17:1D:3C:78:9F:4C:A2:6E:89:F0:21:5A',
        fingerprint: 'SHA256: A1:B2:C3:D4:E5:F6:A7:B8:C9:D0:E1:F2:A3:B4:C5:D6:E7:F8:A9:B0:C1:D2:E3:F4:A5:B6:C7:D8:E9:F0:A1:B2',
        subjectAlternativeNames: [domain, `*.${domain}`, `www.${domain}`],
        isWildcard: domain.includes('*'),
        isExtendedValidation: Math.random() > 0.7,
        trustChain: [
          {
            subject: `CN=${domain}`,
            issuer: 'Let\'s Encrypt Authority X3',
            validTo: '2024-07-15T23:59:59Z'
          },
          {
            subject: 'Let\'s Encrypt Authority X3',
            issuer: 'DST Root CA X3',
            validTo: '2025-09-30T14:01:15Z'
          },
          {
            subject: 'DST Root CA X3',
            issuer: 'DST Root CA X3',
            validTo: '2030-09-30T14:01:15Z'
          }
        ],
        vulnerabilities: []
      };

      // Add some vulnerabilities randomly
      if (Math.random() > 0.8) {
        mockCertificate.vulnerabilities.push({
          type: 'Weak Cipher',
          severity: 'medium',
          description: 'The server supports weak cipher suites that should be disabled.'
        });
      }

      if (mockCertificate.daysUntilExpiry < 30) {
        mockCertificate.vulnerabilities.push({
          type: 'Certificate Expiring Soon',
          severity: 'high',
          description: `Certificate expires in ${mockCertificate.daysUntilExpiry} days.`
        });
      }

      setCertificate(mockCertificate);
      toast.success('SSL certificate checked successfully');
    } catch (error) {
      console.error('SSL check failed:', error);
      setError('Failed to check SSL certificate. Please verify the domain is accessible.');
      toast.error('Failed to check SSL certificate');
    } finally {
      setIsChecking(false);
    }
  };

  // Load example
  const loadExample = () => {
    setUrl('github.com');
    toast.success('Example domain loaded');
  };

  // Format date
  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  // Get security grade
  const getSecurityGrade = () => {
    if (!certificate) return null;

    let score = 100;

    // Deduct points for vulnerabilities
    certificate.vulnerabilities.forEach(vuln => {
      switch (vuln.severity) {
        case 'critical': score -= 30; break;
        case 'high': score -= 20; break;
        case 'medium': score -= 10; break;
        case 'low': score -= 5; break;
      }
    });

    // Deduct points for expiring certificate
    if (certificate.daysUntilExpiry < 30) score -= 15;
    if (certificate.daysUntilExpiry < 7) score -= 25;

    // Deduct points for weak key size
    if (certificate.keySize < 2048) score -= 20;

    if (score >= 90) return { grade: 'A+', color: 'text-green-600', bgColor: 'bg-green-100' };
    if (score >= 80) return { grade: 'A', color: 'text-green-600', bgColor: 'bg-green-100' };
    if (score >= 70) return { grade: 'B', color: 'text-yellow-600', bgColor: 'bg-yellow-100' };
    if (score >= 60) return { grade: 'C', color: 'text-orange-600', bgColor: 'bg-orange-100' };
    return { grade: 'F', color: 'text-red-600', bgColor: 'bg-red-100' };
  };

  const securityGrade = getSecurityGrade();

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-50 to-indigo-100 py-8">
      <div className="container mx-auto px-4 max-w-6xl">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="bg-white rounded-2xl shadow-xl p-6 md:p-8"
        >
          <div className="text-center mb-8">
            <div className="inline-flex items-center justify-center w-16 h-16 bg-purple-100 rounded-full mb-4">
              <Shield className="w-8 h-8 text-purple-600" />
            </div>
            <h1 className="text-3xl font-bold text-gray-900 mb-2">SSL Certificate Checker</h1>
            <p className="text-gray-600 max-w-2xl mx-auto">
              Verify SSL certificate validity, check expiration dates, and analyze security details for any website.
            </p>
          </div>

          <AdContainer 
            position="header"
            adSlot={GOOGLE_ADSENSE_CONFIG.adSlots.imageConverter.header}
            className="mb-6"
          />

          {/* URL Input */}
          <div className="bg-gray-50 rounded-lg p-6 mb-8">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Check SSL Certificate</h3>
            <div className="flex gap-3">
              <input
                type="text"
                value={url}
                onChange={(e) => setUrl(e.target.value)}
                placeholder="example.com or https://example.com"
                className="flex-1 p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                onKeyPress={(e) => e.key === 'Enter' && checkSSLCertificate()}
              />
              <button
                onClick={loadExample}
                className="bg-blue-600 text-white px-6 py-3 rounded-lg hover:bg-blue-700 transition-colors"
              >
                Example
              </button>
              <button
                onClick={checkSSLCertificate}
                disabled={isChecking || !url}
                className="flex items-center gap-2 bg-purple-600 text-white px-6 py-3 rounded-lg hover:bg-purple-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                {isChecking ? (
                  <RefreshCw className="w-4 h-4 animate-spin" />
                ) : (
                  <Shield className="w-4 h-4" />
                )}
                Check SSL
              </button>
            </div>
          </div>

          {/* Results */}
          {error && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-6 mb-8">
              <div className="flex items-center gap-3">
                <XCircle className="w-6 h-6 text-red-600" />
                <div>
                  <h3 className="font-semibold text-red-900">SSL Check Failed</h3>
                  <p className="text-red-700">{error}</p>
                </div>
              </div>
            </div>
          )}

          {certificate && (
            <div className="space-y-6 mb-8">
              {/* Overall Status */}
              <div className="bg-white border border-gray-200 rounded-lg p-6">
                <div className="flex items-center justify-between mb-6">
                  <div className="flex items-center gap-3">
                    {certificate.isValid ? (
                      <CheckCircle className="w-8 h-8 text-green-600" />
                    ) : (
                      <XCircle className="w-8 h-8 text-red-600" />
                    )}
                    <div>
                      <h3 className="text-xl font-bold">
                        {certificate.isValid ? 'Valid SSL Certificate' : 'Invalid SSL Certificate'}
                      </h3>
                      <p className="text-gray-600">{certificate.domain}</p>
                    </div>
                  </div>

                  {securityGrade && (
                    <div className={`px-4 py-2 rounded-lg ${securityGrade.bgColor}`}>
                      <div className={`text-2xl font-bold ${securityGrade.color}`}>
                        {securityGrade.grade}
                      </div>
                      <div className="text-xs text-gray-600">Security Grade</div>
                    </div>
                  )}
                </div>

                <div className="grid md:grid-cols-3 gap-4">
                  <div className="bg-gray-50 rounded-lg p-4">
                    <div className="flex items-center gap-2 mb-2">
                      <Calendar className="w-5 h-5 text-gray-600" />
                      <span className="font-semibold">Expires In</span>
                    </div>
                    <div className={`text-lg font-bold ${
                      certificate.daysUntilExpiry < 7 ? 'text-red-600' :
                      certificate.daysUntilExpiry < 30 ? 'text-yellow-600' : 'text-green-600'
                    }`}>
                      {certificate.daysUntilExpiry} days
                    </div>
                    <div className="text-sm text-gray-600">
                      {formatDate(certificate.validTo)}
                    </div>
                  </div>

                  <div className="bg-gray-50 rounded-lg p-4">
                    <div className="flex items-center gap-2 mb-2">
                      <Lock className="w-5 h-5 text-gray-600" />
                      <span className="font-semibold">Encryption</span>
                    </div>
                    <div className="text-lg font-bold">{certificate.algorithm}</div>
                    <div className="text-sm text-gray-600">
                      {certificate.keySize} bit key
                    </div>
                  </div>

                  <div className="bg-gray-50 rounded-lg p-4">
                    <div className="flex items-center gap-2 mb-2">
                      <Globe className="w-5 h-5 text-gray-600" />
                      <span className="font-semibold">Coverage</span>
                    </div>
                    <div className="text-lg font-bold">
                      {certificate.subjectAlternativeNames.length} domains
                    </div>
                    <div className="text-sm text-gray-600">
                      {certificate.isWildcard ? 'Wildcard' : 'Single domain'}
                    </div>
                  </div>
                </div>
              </div>

              {/* Certificate Details */}
              <div className="grid lg:grid-cols-2 gap-6">
                <div className="bg-blue-50 rounded-lg p-6">
                  <h4 className="font-semibold text-blue-900 mb-4">Certificate Information</h4>
                  <div className="space-y-3">
                    <div>
                      <span className="text-sm font-medium text-gray-700">Issued To:</span>
                      <div className="text-sm text-gray-900 font-mono">{certificate.subject}</div>
                    </div>
                    <div>
                      <span className="text-sm font-medium text-gray-700">Issued By:</span>
                      <div className="text-sm text-gray-900">{certificate.issuer}</div>
                    </div>
                    <div>
                      <span className="text-sm font-medium text-gray-700">Valid From:</span>
                      <div className="text-sm text-gray-900">{formatDate(certificate.validFrom)}</div>
                    </div>
                    <div>
                      <span className="text-sm font-medium text-gray-700">Valid Until:</span>
                      <div className="text-sm text-gray-900">{formatDate(certificate.validTo)}</div>
                    </div>
                    <div>
                      <span className="text-sm font-medium text-gray-700">Serial Number:</span>
                      <div className="text-xs text-gray-900 font-mono break-all">{certificate.serialNumber}</div>
                    </div>
                  </div>
                </div>

                <div className="bg-green-50 rounded-lg p-6">
                  <h4 className="font-semibold text-green-900 mb-4">Security Details</h4>
                  <div className="space-y-3">
                    <div>
                      <span className="text-sm font-medium text-gray-700">Signature Algorithm:</span>
                      <div className="text-sm text-gray-900">{certificate.algorithm}</div>
                    </div>
                    <div>
                      <span className="text-sm font-medium text-gray-700">Key Size:</span>
                      <div className="text-sm text-gray-900">{certificate.keySize} bits</div>
                    </div>
                    <div>
                      <span className="text-sm font-medium text-gray-700">Extended Validation:</span>
                      <div className="text-sm text-gray-900">
                        {certificate.isExtendedValidation ? 'Yes' : 'No'}
                      </div>
                    </div>
                    <div>
                      <span className="text-sm font-medium text-gray-700">Wildcard Certificate:</span>
                      <div className="text-sm text-gray-900">
                        {certificate.isWildcard ? 'Yes' : 'No'}
                      </div>
                    </div>
                    <div>
                      <span className="text-sm font-medium text-gray-700">Fingerprint:</span>
                      <div className="text-xs text-gray-900 font-mono break-all">{certificate.fingerprint}</div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Subject Alternative Names */}
              <div className="bg-yellow-50 rounded-lg p-6">
                <h4 className="font-semibold text-yellow-900 mb-4">Covered Domains</h4>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2">
                  {certificate.subjectAlternativeNames.map((domain, index) => (
                    <div key={index} className="bg-white rounded px-3 py-2 text-sm font-mono">
                      {domain}
                    </div>
                  ))}
                </div>
              </div>

              {/* Trust Chain */}
              <div className="bg-purple-50 rounded-lg p-6">
                <h4 className="font-semibold text-purple-900 mb-4">Certificate Chain</h4>
                <div className="space-y-3">
                  {certificate.trustChain.map((cert, index) => (
                    <div key={index} className="bg-white rounded-lg p-4 border-l-4 border-purple-300">
                      <div className="flex items-center justify-between">
                        <div>
                          <div className="font-medium text-gray-900">{cert.subject}</div>
                          <div className="text-sm text-gray-600">Issued by: {cert.issuer}</div>
                        </div>
                        <div className="text-sm text-gray-500">
                          Valid until: {formatDate(cert.validTo)}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Vulnerabilities */}
              {certificate.vulnerabilities.length > 0 && (
                <div className="bg-red-50 rounded-lg p-6">
                  <h4 className="font-semibold text-red-900 mb-4 flex items-center gap-2">
                    <AlertTriangle className="w-5 h-5" />
                    Security Issues Found
                  </h4>
                  <div className="space-y-3">
                    {certificate.vulnerabilities.map((vuln, index) => (
                      <div key={index} className="bg-white rounded-lg p-4 border-l-4 border-red-400">
                        <div className="flex items-center justify-between mb-2">
                          <span className="font-medium text-gray-900">{vuln.type}</span>
                          <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                            vuln.severity === 'critical' ? 'bg-red-100 text-red-800' :
                            vuln.severity === 'high' ? 'bg-orange-100 text-orange-800' :
                            vuln.severity === 'medium' ? 'bg-yellow-100 text-yellow-800' :
                            'bg-gray-100 text-gray-800'
                          }`}>
                            {vuln.severity.toUpperCase()}
                          </span>
                        </div>
                        <p className="text-sm text-gray-700">{vuln.description}</p>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}

          <AdContainer 
            position="footer"
            adSlot={GOOGLE_ADSENSE_CONFIG.adSlots.imageConverter.footer}
            className="mb-6"
          />

          {/* Educational Content */}
          <div className="grid md:grid-cols-2 gap-6">
            <div className="bg-blue-50 rounded-lg p-6">
              <h4 className="font-semibold text-blue-900 mb-3">SSL Certificate Best Practices</h4>
              <ul className="text-sm text-blue-800 space-y-2">
                <li>• <strong>Use Strong Encryption:</strong> RSA 2048-bit or ECC 256-bit minimum</li>
                <li>• <strong>Regular Renewal:</strong> Renew certificates before expiration</li>
                <li>• <strong>Wildcard Certificates:</strong> Cover all subdomains efficiently</li>
                <li>• <strong>HSTS Headers:</strong> Force HTTPS connections</li>
                <li>• <strong>Certificate Transparency:</strong> Monitor CT logs for unauthorized certificates</li>
                <li>• <strong>Proper Chain:</strong> Ensure complete certificate chain</li>
              </ul>
            </div>

            <div className="bg-green-50 rounded-lg p-6">
              <h4 className="font-semibold text-green-900 mb-3">Common SSL Issues</h4>
              <ul className="text-sm text-green-800 space-y-2">
                <li>• <strong>Expired Certificates:</strong> Monitor expiration dates</li>
                <li>• <strong>Incomplete Chain:</strong> Missing intermediate certificates</li>
                <li>• <strong>Wrong Domain:</strong> Certificate doesn't match domain</li>
                <li>• <strong>Mixed Content:</strong> HTTP resources on HTTPS pages</li>
                <li>• <strong>Weak Ciphers:</strong> Outdated encryption algorithms</li>
                <li>• <strong>Self-Signed Certificates:</strong> Not trusted by browsers</li>
              </ul>
            </div>

            <div className="bg-purple-50 rounded-lg p-6">
              <h4 className="font-semibold text-purple-900 mb-3">SSL Testing Tools</h4>
              <div className="space-y-3">
                <a
                  href="https://www.ssllabs.com/ssltest/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-2 text-purple-700 hover:text-purple-800"
                >
                  <ExternalLink className="w-4 h-4" />
                  SSL Labs Server Test
                </a>
                <a
                  href="https://www.whynopadlock.com/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-2 text-purple-700 hover:text-purple-800"
                >
                  <ExternalLink className="w-4 h-4" />
                  Why No Padlock?
                </a>
                <a
                  href="https://observatory.mozilla.org/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-2 text-purple-700 hover:text-purple-800"
                >
                  <ExternalLink className="w-4 h-4" />
                  Mozilla Observatory
                </a>
              </div>
            </div>

            <div className="bg-yellow-50 rounded-lg p-6">
              <h4 className="font-semibold text-yellow-900 mb-3">Certificate Types</h4>
              <ul className="text-sm text-yellow-800 space-y-2">
                <li>• <strong>Domain Validated (DV):</strong> Basic validation, quick issuance</li>
                <li>• <strong>Organization Validated (OV):</strong> Business verification required</li>
                <li>• <strong>Extended Validation (EV):</strong> Highest level of validation</li>
                <li>• <strong>Wildcard:</strong> Covers domain and all subdomains</li>
                <li>• <strong>Multi-Domain (SAN):</strong> Covers multiple specific domains</li>
                <li>• <strong>Code Signing:</strong> For software and application signing</li>
              </ul>
            </div>
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default SSLCertificateChecker;
